import React, { useState, useEffect, useRef } from 'react';
import { X, Calendar, Search, Check, AlertTriangle, Image as ImageIcon, Trash2 } from 'lucide-react';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { useNotification } from '@/contexts/NotificationContext';
import { useLocationFilter } from '@/contexts/LocationFilterContext';
import { useTaskCategories } from '@/hooks/useTaskCategories';
import { locationService, Location } from '@/firebase/locationService';
import { assetService, Asset } from '@/firebase/assetService';
import { Timestamp } from 'firebase/firestore';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { calculateRiskWeight } from '@/firebase/complianceService';
import { OWNER_ROLES } from '@/constants/roles';
import { getOwnerRolesForOrganizationType, getDefaultOwnerRoleForOrganizationType } from '@/constants/organizationRoles';
import { trackTask } from '@/utils/analytics';
import { 
  uploadMultipleTaskReferenceImages, 
  createReferenceImageMetadata,
  type TaskReferenceImageUploadContext 
} from '@/firebase/taskReferenceImageService';

interface AddTaskPanelProps {
  onClose: () => void;
  onTaskCreated: (taskIds?: string[]) => void;
  prepopulatedLocationId?: string;
  prepopulatedOwnerRole?: string;
  prepopulatedTaskName?: string;
  prepopulatedReferenceImageUrls?: string[]; // Image URLs from message attachments
  prepopulatedAssetIds?: string[]; // Asset IDs to tag the task to (e.g. when creating from asset details page)
  isFromChecklist?: boolean;
}

interface EvidenceType {
  type: string;
  label: string;
  required: boolean;
}

interface User {
  id: string;
  name: string;
  email: string;
  picture?: string;
}

const EVIDENCE_TYPES = [
  'pdf',
  'photo',
  'log',
  'certificate',
  'report',
  'inspection',
  'manifest'
];

const AddTaskPanel: React.FC<AddTaskPanelProps> = ({ 
  onClose, 
  onTaskCreated,
  prepopulatedLocationId,
  prepopulatedOwnerRole,
  prepopulatedTaskName,
  prepopulatedReferenceImageUrls = [],
  prepopulatedAssetIds = [],
  isFromChecklist = false
}) => {
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();
  
  // Get organization-type-specific owner roles
  const organizationType = currentOrganization?.industryType || null;
  const availableOwnerRoles = getOwnerRolesForOrganizationType(organizationType);
  const defaultOwnerRole = getDefaultOwnerRoleForOrganizationType(organizationType);
  const { showNotification } = useNotification();
  const { availableLocations, selectedLocationIds, hasActiveFilters } = useLocationFilter();
  const { categories: taskCategories, addCustomCategory } = useTaskCategories();
  
  const [formData, setFormData] = useState({
    templateName: '',
    locationIds: [] as string[],
    dueDate: '',
    category: '',
    riskLevel: 'Medium' as 'High' | 'Medium' | 'Low',
    ownerRoles: [] as string[],
    assignedUserIds: [] as string[],
    assetIds: [] as string[],
    description: '',
    passFailCriteria: '',
    evidenceTypes: [] as EvidenceType[],
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [locationSearchTerm, setLocationSearchTerm] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [assets, setAssets] = useState<Asset[]>([]);
  const [assetSearchTerm, setAssetSearchTerm] = useState('');
  const [showAssetSection, setShowAssetSection] = useState(prepopulatedAssetIds.length > 0);
  const [evidenceType, setEvidenceType] = useState<EvidenceType>({ type: '', label: '', required: false });
  const [showAddCategoryInput, setShowAddCategoryInput] = useState(false);
  const [newCategoryLabel, setNewCategoryLabel] = useState('');
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  
  // Reference images state
  const [referenceImages, setReferenceImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [prepopulatedImageUrls, setPrepopulatedImageUrls] = useState<string[]>(prepopulatedReferenceImageUrls);
  const [uploadingImages, setUploadingImages] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Two-step flow state: 'form' or 'notes'
  const [currentStep, setCurrentStep] = useState<'form' | 'notes'>('form');
  // Notes for reference images (keyed by image identifier: file name or URL index)
  const [referenceImageNotes, setReferenceImageNotes] = useState<Map<string, string>>(new Map());

  // Calculate smart default alert times based on due date proximity
  const getSmartAlertTimes = (dueDateString: string): number[] => {
    if (!dueDateString) {
      return [30, 14, 7, 1, 0]; // Default for far future dates
    }
    
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Parse due date (YYYY-MM-DD format)
      const dateParts = dueDateString.split('-');
      if (dateParts.length !== 3) {
        return [30, 14, 7, 1, 0];
      }
      
      const year = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10) - 1; // Month is 0-indexed
      const day = parseInt(dateParts[2], 10);
      const dueDate = new Date(year, month, day);
      dueDate.setHours(0, 0, 0, 0);
      
      const daysUntilDue = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      
      // Smart defaults based on how far in the future the due date is
      if (daysUntilDue <= 1) {
        // Due today or tomorrow - alert on due date only
        return [0];
      } else if (daysUntilDue <= 3) {
        // Due within 3 days - alert 1 day before and on due date
        return [1, 0];
      } else if (daysUntilDue <= 7) {
        // Due within a week - alert 3 days, 1 day, and on due date
        return [3, 1, 0];
      } else if (daysUntilDue <= 14) {
        // Due within 2 weeks - alert weekly, then daily
        return [7, 3, 1, 0];
      } else if (daysUntilDue <= 30) {
        // Due within a month - standard alerts
        return [14, 7, 3, 1, 0];
      } else {
        // Due far in future - full alert schedule
        return [30, 14, 7, 1, 0];
      }
    } catch (error) {
      console.error('Error calculating smart alert times:', error);
      return [30, 14, 7, 1, 0]; // Fallback to default
    }
  };

  // Get alert dates (actual dates when alerts will be sent)
  const getAlertDates = (dueDateString: string): Array<{ days: number; date: Date; label: string }> => {
    if (!dueDateString) {
      return [];
    }
    
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Parse due date
      const dateParts = dueDateString.split('-');
      if (dateParts.length !== 3) {
        return [];
      }
      
      const year = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10) - 1;
      const day = parseInt(dateParts[2], 10);
      const dueDate = new Date(year, month, day);
      dueDate.setHours(0, 0, 0, 0);
      
      const alertTimes = getSmartAlertTimes(dueDateString);
      
      return alertTimes.map((days) => {
        const alertDate = new Date(dueDate);
        alertDate.setDate(alertDate.getDate() - days);
        
        let label: string;
        if (days === 0) {
          label = 'Due Date';
        } else if (days === 1) {
          label = '1 day before';
        } else {
          label = `${days} days before`;
        }
        
        return {
          days,
          date: alertDate,
          label
        };
      }).sort((a, b) => b.days - a.days); // Sort by days (descending)
    } catch (error) {
      console.error('Error calculating alert dates:', error);
      return [];
    }
  };

  useEffect(() => {
    loadLocations();
    loadUsers();
  }, [currentOrganization?.id, hasActiveFilters, selectedLocationIds, availableLocations]);

  // Pre-select locations from global filter if active, or from prepopulated props
  useEffect(() => {
    if (prepopulatedLocationId) {
      // If prepopulated location is provided, use it
      setFormData(prev => ({
        ...prev,
        locationIds: [prepopulatedLocationId]
      }));
    } else if (hasActiveFilters && selectedLocationIds.length > 0) {
      setFormData(prev => ({
        ...prev,
        locationIds: selectedLocationIds
      }));
    }
  }, [hasActiveFilters, selectedLocationIds, prepopulatedLocationId]);

  // Pre-populate asset IDs when creating task from asset details page
  useEffect(() => {
    if (prepopulatedAssetIds.length > 0) {
      setFormData(prev => ({ ...prev, assetIds: [...prepopulatedAssetIds] }));
    }
  }, [prepopulatedAssetIds.join(',')]);

  // Pre-populate owner roles, task name, and category if provided
  useEffect(() => {
    if (prepopulatedOwnerRole || prepopulatedTaskName || isFromChecklist) {
      setFormData(prev => ({
        ...prev,
        ...(prepopulatedOwnerRole && { ownerRoles: [prepopulatedOwnerRole] }),
        // If no prepopulated role and default role exists, use it
        ...(!prepopulatedOwnerRole && defaultOwnerRole && { ownerRoles: [defaultOwnerRole] }),
        ...(prepopulatedTaskName && { templateName: prepopulatedTaskName }),
        // Set default category to "Operational" when from checklist
        ...(isFromChecklist && !prev.category && { category: 'Operational' })
      }));
    }
  }, [prepopulatedOwnerRole, prepopulatedTaskName, isFromChecklist, defaultOwnerRole]);

  const loadLocations = async () => {
    // Always show all available locations - users can select from all
    if (availableLocations.length > 0) {
      setLocations(availableLocations);
    } else if (currentOrganization?.id && user?.id) {
      // Load locations if not already loaded
      const { doc, getDoc } = await import('firebase/firestore');
      let userLocationIds: string[] | undefined;
      let userRole: string | undefined;
      
      const userRef = doc(db, 'users', user.id);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const userData = userSnap.data();
        userLocationIds = userData.locationIds;
        userRole = userData.role;
      }

      const result = await locationService.getAccessibleLocations(
        currentOrganization.id,
        user.id,
        userRole,
        userLocationIds
      );
      if (result.success && result.data) {
        setLocations(result.data.filter(loc => loc.id && loc.name));
      }
    }
  };

  const loadAssets = async () => {
    const locationIds = formData.locationIds.length > 0 ? formData.locationIds : (prepopulatedLocationId ? [prepopulatedLocationId] : []);
    if (!currentOrganization?.id || locationIds.length === 0) {
      setAssets([]);
      return;
    }
    try {
      const results = await Promise.all(
        locationIds.map(locId => assetService.getAssetsByLocation(locId, currentOrganization.id))
      );
      const byId = new Map<string, Asset>();
      results.forEach(r => {
        if (r.success && r.data) {
          r.data.forEach(a => { if (a.id) byId.set(a.id, a); });
        }
      });
      setAssets(Array.from(byId.values()));
    } catch (err) {
      console.error('Error loading assets:', err);
      setAssets([]);
    }
  };

  useEffect(() => {
    loadAssets();
  }, [formData.locationIds.join(','), currentOrganization?.id, prepopulatedLocationId]);

  const loadUsers = async () => {
    if (!currentOrganization?.id) {
      setUsers([]);
      return;
    }

    try {
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      const { db } = await import('@/firebase/config');
      
      const usersRef = collection(db, 'users');
      
      // Query 1: Users with organizationId matching current organization (legacy support)
      const orgIdQuery = query(usersRef, where('organizationId', '==', currentOrganization.id));
      
      // Query 2: Users with organizationIds array containing current organization
      const orgIdsQuery = query(usersRef, where('organizationIds', 'array-contains', currentOrganization.id));
      
      // Execute both queries in parallel
      const [orgIdSnapshot, orgIdsSnapshot] = await Promise.all([
        getDocs(orgIdQuery),
        getDocs(orgIdsQuery)
      ]);
      
      // Combine results and deduplicate by user ID
      const usersMap = new Map<string, User>();
      
      // Process users from organizationId query
      orgIdSnapshot.forEach((doc) => {
        const data = doc.data();
        usersMap.set(doc.id, {
          id: doc.id,
          name: data.name || data.displayName || '',
          email: data.email || '',
          picture: data.picture,
        });
      });
      
      // Process users from organizationIds array query (skip duplicates)
      orgIdsSnapshot.forEach((doc) => {
        if (usersMap.has(doc.id)) return; // Skip if already added
        const data = doc.data();
        usersMap.set(doc.id, {
          id: doc.id,
          name: data.name || data.displayName || '',
          email: data.email || '',
          picture: data.picture,
        });
      });
      
      // Convert map to array
      const usersList = Array.from(usersMap.values());
      setUsers(usersList);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  // Filter locations based on search term
  const filteredLocations = locations.filter(location =>
    location.name.toLowerCase().includes(locationSearchTerm.toLowerCase()) ||
    location.address.toLowerCase().includes(locationSearchTerm.toLowerCase()) ||
    location.city?.toLowerCase().includes(locationSearchTerm.toLowerCase()) ||
    location.state.toLowerCase().includes(locationSearchTerm.toLowerCase()) ||
    location.country.toLowerCase().includes(locationSearchTerm.toLowerCase())
  );

  // Filter users based on search term
  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(userSearchTerm.toLowerCase())
  );

  const toggleLocation = (locationId: string) => {
    setFormData(prev => {
      if (prev.locationIds.includes(locationId)) {
        return {
          ...prev,
          locationIds: prev.locationIds.filter(id => id !== locationId)
        };
      } else {
        return {
          ...prev,
          locationIds: [...prev.locationIds, locationId]
        };
      }
    });
  };

  const toggleUser = (userId: string) => {
    setFormData(prev => {
      if (prev.assignedUserIds.includes(userId)) {
        return {
          ...prev,
          assignedUserIds: prev.assignedUserIds.filter(id => id !== userId)
        };
      } else {
        return {
          ...prev,
          assignedUserIds: [...prev.assignedUserIds, userId]
        };
      }
    });
  };

  const addEvidenceType = () => {
    if (!evidenceType.type || !evidenceType.label) {
      showNotification('Please enter both type and label for evidence', 'error');
      return;
    }
    setFormData(prev => ({
      ...prev,
      evidenceTypes: [...prev.evidenceTypes, { ...evidenceType }]
    }));
    setEvidenceType({ type: '', label: '', required: false });
  };

  const removeEvidenceType = (index: number) => {
    setFormData(prev => ({
      ...prev,
      evidenceTypes: prev.evidenceTypes.filter((_, i) => i !== index)
    }));
  };

  // Reference image handling functions
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newFiles: File[] = [];
    const newPreviews: string[] = [];

    // Validate and process each file
    Array.from(files).forEach((file) => {
      // Check file type
      if (!file.type.startsWith('image/')) {
        showNotification(`File "${file.name}" is not an image. Please select image files only.`, 'error');
        return;
      }

      // Check file size (10MB max)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        showNotification(`File "${file.name}" is too large. Maximum size is 10MB.`, 'error');
        return;
      }

      // Check total number of images (max 10) - including prepopulated images
      if (prepopulatedImageUrls.length + referenceImages.length + newFiles.length > 10) {
        showNotification('Maximum 10 reference images allowed per task.', 'error');
        return;
      }

      newFiles.push(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        if (result) {
          setImagePreviews(prev => [...prev, result]);
        }
      };
      reader.readAsDataURL(file);
    });

    if (newFiles.length > 0) {
      setReferenceImages(prev => [...prev, ...newFiles]);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeImage = (index: number) => {
    const fileToRemove = referenceImages[index];
    if (fileToRemove) {
      // Remove note for this file
      setReferenceImageNotes(prev => {
        const updated = new Map(prev);
        updated.delete(fileToRemove.name);
        return updated;
      });
    }
    setReferenceImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const removePrepopulatedImage = (index: number) => {
    const urlToRemove = prepopulatedImageUrls[index];
    // Remove note for this prepopulated image (using URL as key)
    if (urlToRemove) {
      setReferenceImageNotes(prev => {
        const updated = new Map(prev);
        updated.delete(urlToRemove);
        return updated;
      });
    }
    setPrepopulatedImageUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.locationIds.length === 0) {
      showNotification('Please select at least one location', 'error');
      return;
    }
    
    if (!formData.dueDate) {
      showNotification('Please select a due date', 'error');
      return;
    }

    if (!formData.category) {
      showNotification('Please select a category', 'error');
      return;
    }
    
    if (!currentOrganization?.id) {
      showNotification('Organization is required', 'error');
      return;
    }

    // If there are reference images, go to notes step
    if (hasReferenceImages) {
      setCurrentStep('notes');
    } else {
      // No images, proceed directly to create task
      handleCreateTask(e);
    }
  };

  const handleBack = () => {
    setCurrentStep('form');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    handleCreateTask(e);
  };

  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.locationIds.length === 0) {
      showNotification('Please select at least one location', 'error');
      return;
    }
    
    if (!formData.dueDate) {
      showNotification('Please select a due date', 'error');
      return;
    }

    if (!formData.category) {
      showNotification('Please select a category', 'error');
      return;
    }
    
    if (!currentOrganization?.id) {
      showNotification('Organization is required', 'error');
      return;
    }

    setIsSubmitting(true);
    setUploadingImages(true);
    try {
      // Upload reference images first (if any)
      let referenceImageUrls: string[] = [];
      let referenceImageFileNames: string[] = [];
      let referenceImageMetadata: Array<{
        url: string;
        fileName: string;
        uploadedAt: number;
        uploadedBy: string;
        uploadedByName?: string;
      }> = [];

      if (referenceImages.length > 0 && user?.id && currentOrganization?.id) {
        try {
          // We'll upload images after creating tasks, so we need a temporary task ID
          // For now, we'll create tasks first, then upload images to each task
          showNotification('Uploading reference images...', 'info');
        } catch (imageError: any) {
          console.error('Error preparing image upload:', imageError);
          showNotification(`Error with images: ${imageError.message}`, 'error');
          setIsSubmitting(false);
          setUploadingImages(false);
          return;
        }
      }

      // Parse date string (YYYY-MM-DD) and create Date in local timezone at midnight
      // This prevents timezone issues where UTC midnight becomes previous day in some timezones
      const dateParts = formData.dueDate.split('-');
      if (dateParts.length !== 3) {
        showNotification('Invalid due date format', 'error');
        setIsSubmitting(false);
        setUploadingImages(false);
        return;
      }
      const year = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10) - 1; // Month is 0-indexed
      const day = parseInt(dateParts[2], 10);
      const dueDate = new Date(year, month, day, 23, 59, 59, 999); // End of day in local timezone
      if (isNaN(dueDate.getTime())) {
        showNotification('Invalid due date', 'error');
        setIsSubmitting(false);
        setUploadingImages(false);
        return;
      }

      // Create tasks for each selected location
      const tasksRef = collection(db, 'tasks');
      // Calculate smart alert times based on due date
      const smartAlertTimes = getSmartAlertTimes(formData.dueDate);
      
      const ownerRolesList = (formData.ownerRoles || []).filter((r: string) => r && r.trim() !== '');
      const firstOwnerRole = ownerRolesList.length > 0 ? ownerRolesList[0] : '';

      const taskPromises = formData.locationIds.map(async (locationId) => {
        // Create template snapshot for standalone task (same shape as Android)
        const templateSnapshot = {
          name: formData.templateName,
          category: formData.category,
          risk_level: formData.riskLevel,
          risk_weight: calculateRiskWeight(formData.riskLevel),
          evidence_types: formData.evidenceTypes,
          pass_fail_criteria: formData.passFailCriteria || '',
          jurisdiction: '',
          jurisdiction_notes: '',
          owner_role: firstOwnerRole,
          lead_times_days: smartAlertTimes, // Use smart defaults based on due date
          frequency: 'P1Y', // Default for ad-hoc tasks
        };

        // Build task data (same owner role fields as Android: ownerRoles, ownerRole, owner_role)
        const taskData: any = {
          templateId: null,
          originalTemplateId: null,
          name: formData.templateName,
          title: formData.templateName,
          task: formData.templateName,
          templateName: formData.templateName,
          locationId: locationId,
          status: 'pending', // Always pending for new tasks
          dueDate: Timestamp.fromDate(dueDate),
          frequency: 'P1Y',
          category: formData.category,
          riskLevel: formData.riskLevel,
          organizationId: currentOrganization.id,
          templateSnapshot: templateSnapshot,
          taskType: 'custom',
          isCustom: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
        
        // Set createdBy and createdByName for proper task tracking in apps
        if (user?.id) {
          taskData.createdBy = user.id;
          taskData.createdByName = user.name || user.email || '';
        }
        
        // Set owner role fields (same as Android app)
        if (ownerRolesList.length > 0) {
          taskData.ownerRoles = [...ownerRolesList];
          taskData.ownerRole = firstOwnerRole;
          taskData.owner_role = firstOwnerRole;
        }
        
        // Only add assignedUserIds if there are any assigned users
        if (formData.assignedUserIds.length > 0) {
          taskData.assignedUserIds = formData.assignedUserIds;
          // Also add single user field for backward compatibility if only one user
          if (formData.assignedUserIds.length === 1) {
            taskData.assignedUserId = formData.assignedUserIds[0];
          }
        }

        // Tag task to selected assets (for task-by-asset queries and asset details page)
        if ((formData.assetIds ?? []).length > 0) {
          taskData.assetIds = [...(formData.assetIds ?? [])];
        }

        return addDoc(tasksRef, taskData);
      });

      const taskDocs = await Promise.all(taskPromises);
      const createdTaskIds = taskDocs.map(doc => doc.id);

      // Upload reference images to each created task (only new files, not prepopulated URLs)
      if (referenceImages.length > 0 && user?.id && currentOrganization?.id) {
        try {
          const uploadContext: TaskReferenceImageUploadContext = {
            taskId: createdTaskIds[0], // Upload to first task, then copy URLs to others
            organizationId: currentOrganization.id,
            locationId: formData.locationIds[0],
            uploadedBy: {
              id: user.id,
              email: user.email || '',
              name: user.name || '',
            },
          };

          const uploadResults = await uploadMultipleTaskReferenceImages(referenceImages, uploadContext);
          
          const uploadedUrls = uploadResults.map(result => result.url);
          const uploadedFileNames = referenceImages.map(file => file.name);
          const uploadedMetadata = uploadResults.map((result, index) => {
            const metadata = createReferenceImageMetadata(result, referenceImages[index].name, {
              id: user.id,
              email: user.email || '',
              name: user.name || '',
            });
            // Ensure uploadedAt is a number
            const baseMetadata = {
              ...metadata,
              uploadedAt: typeof metadata.uploadedAt === 'number' ? metadata.uploadedAt : metadata.uploadedAt.getTime(),
            };
            // Add note if available
            const note = referenceImageNotes.get(referenceImages[index].name);
            if (note && note.trim()) {
              return {
                ...baseMetadata,
                note: note.trim(),
              };
            }
            return baseMetadata;
          });

          // Combine prepopulated URLs with newly uploaded URLs
          referenceImageUrls = [...prepopulatedImageUrls, ...uploadedUrls];
          referenceImageFileNames = [...prepopulatedImageUrls.map((_, i) => `message-image-${i + 1}.jpg`), ...uploadedFileNames];
          referenceImageMetadata = [
            // For prepopulated images, create basic metadata
            ...prepopulatedImageUrls.map((url, i) => {
              const note = referenceImageNotes.get(url);
              const baseMetadata = {
                url,
                fileName: `message-image-${i + 1}.jpg`,
                uploadedAt: Date.now(),
                uploadedBy: user.id,
                uploadedByName: user.name || user.email || '',
              };
              if (note && note.trim()) {
                return {
                  ...baseMetadata,
                  note: note.trim(),
                };
              }
              return baseMetadata;
            }),
            ...uploadedMetadata
          ];
        } catch (imageError: any) {
          console.error('Error uploading reference images:', imageError);
          showNotification(`Tasks created but image upload failed: ${imageError.message}`, 'error');
          // Continue - tasks are created, just without images
        }
      } else if (prepopulatedImageUrls.length > 0) {
        // Only prepopulated URLs, no new uploads
        referenceImageUrls = prepopulatedImageUrls;
        referenceImageFileNames = prepopulatedImageUrls.map((_, i) => `message-image-${i + 1}.jpg`);
        referenceImageMetadata = prepopulatedImageUrls.map((url, i) => {
          const note = referenceImageNotes.get(url);
          const baseMetadata = {
            url,
            fileName: `message-image-${i + 1}.jpg`,
            uploadedAt: Date.now(),
            uploadedBy: user?.id || '',
            uploadedByName: user?.name || user?.email || '',
          };
          if (note && note.trim()) {
            return {
              ...baseMetadata,
              note: note.trim(),
            };
          }
          return baseMetadata;
        });
      }

      // Update all created tasks with reference image URLs (if any)
      if (referenceImageUrls.length > 0) {
        try {
          const { doc, updateDoc } = await import('firebase/firestore');
          const updatePromises = createdTaskIds.map(taskId => {
            const taskRef = doc(db, 'tasks', taskId);
            return updateDoc(taskRef, {
              referenceImageUrls,
              referenceImageFileNames,
              referenceImageMetadata,
              updatedAt: serverTimestamp(),
            });
          });
          await Promise.all(updatePromises);
        } catch (updateError: any) {
          console.error('Error updating tasks with reference images:', updateError);
          // Continue - tasks are created, just without image metadata
        }
      }

      // Track task creation events
      createdTaskIds.forEach((taskId, index) => {
        trackTask.created(
          formData.category || undefined,
          formData.locationIds[index] || undefined
        );
      });

      const locationsCount = formData.locationIds.length;
      const taskCount = locationsCount;
      const locationText = locationsCount === 1 ? 'location' : 'locations';
      const taskText = taskCount === 1 ? 'task' : 'tasks';
      
      showNotification(`✅ ${taskCount} ${taskText} created successfully for ${locationsCount} ${locationText}`, 'success');
      
      // Refresh the task list and pass created task IDs
      onTaskCreated(createdTaskIds);
      
      // Reset form but keep global filter selections if active
      const initialLocationIds = hasActiveFilters && selectedLocationIds.length > 0 
        ? selectedLocationIds 
        : [];
      
      setFormData({
        templateName: '',
        locationIds: initialLocationIds,
        dueDate: '',
        category: '',
        riskLevel: 'Medium',
        ownerRoles: [],
        assignedUserIds: [],
        assetIds: [],
        description: '',
        passFailCriteria: '',
        evidenceTypes: [],
      });
      
      // Reset search terms
      setLocationSearchTerm('');
      setUserSearchTerm('');
      setEvidenceType({ type: '', label: '', required: false });
      
      // Reset reference images
      setReferenceImages([]);
      setImagePreviews([]);
      setPrepopulatedImageUrls([]);
      setReferenceImageNotes(new Map());
      setCurrentStep('form');
      
      // Scroll to top and focus on task name field
      setTimeout(() => {
        const scrollableDiv = document.querySelector('[data-task-scrollable]');
        if (scrollableDiv) {
          scrollableDiv.scrollTop = 0;
        }
        const taskNameInput = document.querySelector('[data-task-name-input]') as HTMLInputElement;
        if (taskNameInput) {
          taskNameInput.focus();
        }
      }, 100);
    } catch (error: any) {
      console.error('Error creating task:', error);
      showNotification(`Error creating task: ${error.message}`, 'error');
    } finally {
      setIsSubmitting(false);
      setUploadingImages(false);
    }
  };

  // Get today's date in YYYY-MM-DD format for date input
  const today = new Date().toISOString().split('T')[0];

  // Use higher z-index when opened from checklist to appear above checklist modal
  const backdropZIndex = isFromChecklist ? 'z-[140]' : 'z-[115]';
  const panelZIndex = isFromChecklist ? 'z-[150]' : 'z-[120]';
  
  // Check if there are any reference images (check both referenceImages and imagePreviews to handle async updates)
  const hasReferenceImages = prepopulatedImageUrls.length > 0 || referenceImages.length > 0 || imagePreviews.length > 0;
  
  // Compute button text based on whether there are images
  const getButtonText = () => {
    if (isSubmitting) return 'Creating...';
    if (hasReferenceImages) return 'Next';
    return 'Create Task' + (formData.locationIds.length > 1 ? `s (${formData.locationIds.length})` : '');
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black bg-opacity-50 ${backdropZIndex} transition-opacity`}
        onClick={onClose}
      />
      
      {/* Panel */}
      <div className={`fixed inset-y-0 right-0 w-[600px] bg-white shadow-2xl border-l border-gray-200 ${panelZIndex} flex flex-col`}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-800">Add New Task</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-1"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Content - Scrollable */}
        <div className="flex-1 overflow-y-auto" data-task-scrollable>
          <form onSubmit={handleNext} className="p-4 space-y-3" data-task-form>
            {/* Warning Banner - Only show if not from checklist */}
            {!isFromChecklist && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-900 mb-1">
                      Use Policy Templates When Possible
                    </p>
                    <p className="text-xs text-amber-800">
                      To ensure tasks are automatically generated and added on schedule, we recommend using compliance policy templates instead of creating ad-hoc tasks. This helps maintain consistency and ensures all requirements are systematically tracked.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Info Banner - Show if from checklist */}
            {isFromChecklist && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-900 mb-1">
                      Task from Checklist Item
                    </p>
                    <p className="text-xs text-blue-800">
                      Location and Owner Role are pre-filled from the checklist's compliance policy. Review and adjust as needed before creating the task.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Task Name */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Task Name *
              </label>
              <input
                type="text"
                required
                value={formData.templateName}
                onChange={(e) => setFormData({ ...formData, templateName: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                placeholder="e.g., Annual Fire Inspection"
                data-task-name-input
                autoFocus
              />
            </div>

            {/* Reference Images */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Reference Images (Optional)
              </label>
              <div className="border border-gray-300 rounded-md p-3 space-y-3">
                {/* Image Preview Grid - Show prepopulated URLs and uploaded file previews */}
                {(prepopulatedImageUrls.length > 0 || imagePreviews.length > 0) && (
                  <div className="grid grid-cols-3 gap-2">
                    {/* Prepopulated images from message */}
                    {prepopulatedImageUrls.map((url, index) => (
                      <div key={`prepop-${index}`} className="relative group">
                        <img
                          src={url}
                          alt={`Reference image ${index + 1}`}
                          className="w-full h-24 object-cover rounded border border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={() => removePrepopulatedImage(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Remove image"
                        >
                          <X className="w-3 h-3" />
                        </button>
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 truncate">
                          From message
                        </div>
                      </div>
                    ))}
                    {/* Uploaded file previews */}
                    {imagePreviews.map((preview, index) => (
                      <div key={`upload-${index}`} className="relative group">
                        <img
                          src={preview}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-24 object-cover rounded border border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Remove image"
                        >
                          <X className="w-3 h-3" />
                        </button>
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 truncate">
                          {referenceImages[index]?.name || 'Image'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* File Input */}
                <div className="flex items-center gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                    multiple
                    onChange={handleImageSelect}
                    className="hidden"
                    id="reference-images-input"
                    disabled={uploadingImages || isSubmitting || (prepopulatedImageUrls.length + referenceImages.length) >= 10}
                  />
                  <label
                    htmlFor="reference-images-input"
                    className={`flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 transition-colors ${
                      uploadingImages || isSubmitting || (prepopulatedImageUrls.length + referenceImages.length) >= 10
                        ? 'opacity-50 cursor-not-allowed'
                        : ''
                    }`}
                  >
                    <ImageIcon className="w-4 h-4 text-gray-600" />
                    <span>Add Reference Images</span>
                  </label>
                  {(prepopulatedImageUrls.length > 0 || referenceImages.length > 0) && (
                    <span className="text-xs text-gray-500">
                      {prepopulatedImageUrls.length + referenceImages.length} / 10 image{(prepopulatedImageUrls.length + referenceImages.length) !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>

                {/* Help Text */}
                <p className="text-xs text-gray-500">
                  Upload up to 10 reference images (JPEG, PNG, WebP, GIF). Max 10MB per image.
                  {uploadingImages && (
                    <span className="ml-2 text-blue-600">Uploading images...</span>
                  )}
                </p>
              </div>
            </div>

            {/* Location Display - Simplified for checklist */}
            {isFromChecklist && prepopulatedLocationId ? (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Location</label>
                <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                  <span className="text-sm font-medium text-blue-900">
                    {locations.find(loc => loc.id === prepopulatedLocationId)?.name || 'Selected location'}
                  </span>
                </div>
              </div>
            ) : (
              /* Multiple Location Selection with Search */
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Locations * (Select one or more)
                </label>
                {hasActiveFilters && selectedLocationIds.length > 0 && (
                  <p className="text-xs text-primary-600 mb-2">
                    Global filter active: {selectedLocationIds.length} location{selectedLocationIds.length !== 1 ? 's' : ''} pre-selected
                  </p>
                )}
                <div className="relative mb-2">
                  <Search className="absolute left-2 top-2.5 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={locationSearchTerm}
                    onChange={(e) => setLocationSearchTerm(e.target.value)}
                    placeholder="Search locations..."
                    className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  />
                </div>
                <div className="border border-gray-300 rounded-md max-h-48 overflow-y-auto">
                  {filteredLocations.length === 0 ? (
                    <div className="p-3 text-sm text-gray-500 text-center">
                      No locations found
                    </div>
                  ) : (
                    <div className="space-y-1 p-2">
                      {filteredLocations.map(location => {
                        const isSelected = formData.locationIds.includes(location.id!);
                        return (
                          <label
                            key={location.id}
                            className={`
                              flex items-start space-x-2 p-2 rounded cursor-pointer hover:bg-gray-50
                              ${isSelected ? 'bg-primary-50 border border-primary-300' : 'border border-transparent'}
                            `}
                          >
                            <div className="flex items-center justify-center w-5 h-5 mt-0.5">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleLocation(location.id!)}
                                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2">
                                <span className={`text-sm font-medium ${isSelected ? 'text-primary-900' : 'text-gray-900'}`}>
                                  {location.name}
                                </span>
                                {isSelected && (
                                  <Check className="w-4 h-4 text-primary-600 flex-shrink-0" />
                                )}
                              </div>
                              <div className="flex gap-2 mt-1 text-xs text-gray-500">
                                {location.city && <span>{location.city}</span>}
                                {location.city && location.state && <span>•</span>}
                                <span>{location.state}</span>
                                <span>•</span>
                                <span>{location.country}</span>
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
                {formData.locationIds.length > 0 && (
                  <div className="mt-2 text-xs text-gray-600">
                    {formData.locationIds.length} location{formData.locationIds.length !== 1 ? 's' : ''} selected
                  </div>
                )}
              </div>
            )}

            {/* Tag to asset(s) - optional, shown when locations selected or prepopulated */}
            {((formData.locationIds ?? []).length > 0 || (prepopulatedAssetIds ?? []).length > 0) && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowAssetSection(prev => !prev)}
                  className="w-full flex items-center justify-between px-3 py-2 text-left text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100"
                >
                  <span>Tag to asset(s) (Optional)</span>
                  <span className="text-gray-500">{(formData.assetIds ?? []).length > 0 ? `${(formData.assetIds ?? []).length} selected` : ''}</span>
                </button>
                {showAssetSection && (
                  <div className="p-2 border-t border-gray-200 bg-white">
                    <div className="relative mb-2">
                      <Search className="absolute left-2 top-2.5 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={assetSearchTerm}
                        onChange={(e) => setAssetSearchTerm(e.target.value)}
                        placeholder="Search assets..."
                        className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-300 rounded-md"
                      />
                    </div>
                    <div className="border border-gray-300 rounded-md max-h-40 overflow-y-auto">
                      {assets.length === 0 ? (
                        <div className="p-3 text-sm text-gray-500 text-center">
                          {formData.locationIds.length === 0 ? 'Select location(s) first' : 'No assets in selected location(s)'}
                        </div>
                      ) : (
                        <div className="space-y-1 p-2">
                          {assets
                            .filter(a => !assetSearchTerm || (a.assetName || '').toLowerCase().includes(assetSearchTerm.toLowerCase()))
                            .map(asset => {
                              const isSelected = (formData.assetIds ?? []).includes(asset.id!);
                              return (
                                <label
                                  key={asset.id}
                                  className={`flex items-center space-x-2 p-2 rounded cursor-pointer hover:bg-gray-50 ${isSelected ? 'bg-primary-50 border border-primary-300' : 'border border-transparent'}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => {
                                      setFormData(prev => ({
                                        ...prev,
                                        assetIds: isSelected ? (prev.assetIds ?? []).filter(id => id !== asset.id) : [...(prev.assetIds ?? []), asset.id!]
                                      }));
                                    }}
                                    className="w-4 h-4 text-primary-600 border-gray-300 rounded"
                                  />
                                  <span className="text-sm">{asset.assetName || `Asset ${asset.id}`}</span>
                                  {locations.find(l => l.id === asset.locationId) && (
                                    <span className="text-xs text-gray-500">({locations.find(l => l.id === asset.locationId)?.name})</span>
                                  )}
                                </label>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Due Date */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Due Date *
              </label>
              <div className="relative">
                <Calendar className="absolute left-2 top-2.5 w-4 h-4 text-gray-400" />
                <input
                  type="date"
                  required
                  value={formData.dueDate}
                  min={today}
                  onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                  className="w-full pl-9 pr-2 py-1.5 text-sm border border-gray-300 rounded-md"
                />
              </div>
              {/* Alert Times Display */}
              {formData.dueDate && (() => {
                const alertDates = getAlertDates(formData.dueDate);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                
                return (
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-start gap-2">
                      <div className="flex-shrink-0 mt-0.5">
                        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-blue-900 mb-2">
                          Assigned users will receive alerts on:
                        </p>
                        <div className="space-y-1.5">
                          {alertDates.map((alert) => {
                            const isDueDate = alert.days === 0;
                            const isPast = alert.date < today;
                            const isToday = alert.date.getTime() === today.getTime();
                            
                            return (
                              <div
                                key={alert.days}
                                className={`flex items-center justify-between px-2 py-1 rounded text-xs ${
                                  isDueDate
                                    ? 'bg-red-100 border border-red-300'
                                    : isPast
                                    ? 'bg-gray-100 border border-gray-300 opacity-60'
                                    : isToday
                                    ? 'bg-yellow-100 border border-yellow-300'
                                    : 'bg-blue-100 border border-blue-300'
                                }`}
                              >
                                <span className={`font-medium ${
                                  isDueDate
                                    ? 'text-red-800'
                                    : isPast
                                    ? 'text-gray-600'
                                    : isToday
                                    ? 'text-yellow-800'
                                    : 'text-blue-800'
                                }`}>
                                  {alert.label}
                                </span>
                                <span className={`ml-2 ${
                                  isDueDate
                                    ? 'text-red-700'
                                    : isPast
                                    ? 'text-gray-500'
                                    : isToday
                                    ? 'text-yellow-700'
                                    : 'text-blue-700'
                                }`}>
                                  {isToday
                                    ? 'Today'
                                    : isPast
                                    ? alert.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: alert.date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined })
                                    : alert.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: alert.date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined })}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-xs text-blue-700 mt-2 pt-2 border-t border-blue-200">
                          📅 Notifications are sent daily at 8 AM Mountain Time
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Category and Risk Level */}
            {isFromChecklist ? (
              /* Simplified layout for checklist */
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Category *
                  </label>
                  <select
                    required={!showAddCategoryInput}
                    value={showAddCategoryInput ? '' : formData.category}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '__add_new__') {
                        setShowAddCategoryInput(true);
                        setFormData((prev) => ({ ...prev, category: '' }));
                      } else {
                        setShowAddCategoryInput(false);
                        setFormData((prev) => ({ ...prev, category: v }));
                      }
                    }}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                    disabled={showAddCategoryInput}
                  >
                    <option value="">Select...</option>
                    {taskCategories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                    <option value="__add_new__">+ Add new category...</option>
                  </select>
                  {showAddCategoryInput && (
                    <div className="mt-2 flex gap-2">
                      <input
                        type="text"
                        value={newCategoryLabel}
                        onChange={(e) => setNewCategoryLabel(e.target.value)}
                        placeholder="New category name"
                        className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                        maxLength={50}
                      />
                      <button
                        type="button"
                        onClick={async () => {
                          const trimmed = newCategoryLabel.trim();
                          if (!trimmed) {
                            showNotification('Enter a category name', 'error');
                            return;
                          }
                          setIsAddingCategory(true);
                          const result = await addCustomCategory(trimmed);
                          setIsAddingCategory(false);
                          if (result.success) {
                            setFormData((prev) => ({ ...prev, category: trimmed }));
                            setNewCategoryLabel('');
                            setShowAddCategoryInput(false);
                            showNotification('Category added', 'success');
                          } else {
                            showNotification(result.error || 'Failed to add category', 'error');
                          }
                        }}
                        disabled={isAddingCategory}
                        className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                      >
                        {isAddingCategory ? 'Adding...' : 'Add'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddCategoryInput(false);
                          setNewCategoryLabel('');
                        }}
                        className="px-2 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Risk Level *
                  </label>
                  <select
                    required
                    value={formData.riskLevel}
                    onChange={(e) => setFormData({ ...formData, riskLevel: e.target.value as 'High' | 'Medium' | 'Low' })}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                  </select>
                </div>
              </div>
            ) : (
              /* Standard layout for normal task creation */
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Category *
                  </label>
                  <select
                    required={!showAddCategoryInput}
                    value={showAddCategoryInput ? '' : formData.category}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '__add_new__') {
                        setShowAddCategoryInput(true);
                        setFormData((prev) => ({ ...prev, category: '' }));
                      } else {
                        setShowAddCategoryInput(false);
                        setFormData((prev) => ({ ...prev, category: v }));
                      }
                    }}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                    disabled={showAddCategoryInput}
                  >
                    <option value="">Select...</option>
                    {taskCategories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                    <option value="__add_new__">+ Add new category...</option>
                  </select>
                  {showAddCategoryInput && (
                    <div className="mt-2 flex gap-2 flex-wrap">
                      <input
                        type="text"
                        value={newCategoryLabel}
                        onChange={(e) => setNewCategoryLabel(e.target.value)}
                        placeholder="New category name"
                        className="flex-1 min-w-[120px] px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                        maxLength={50}
                      />
                      <button
                        type="button"
                        onClick={async () => {
                          const trimmed = newCategoryLabel.trim();
                          if (!trimmed) {
                            showNotification('Enter a category name', 'error');
                            return;
                          }
                          setIsAddingCategory(true);
                          const result = await addCustomCategory(trimmed);
                          setIsAddingCategory(false);
                          if (result.success) {
                            setFormData((prev) => ({ ...prev, category: trimmed }));
                            setNewCategoryLabel('');
                            setShowAddCategoryInput(false);
                            showNotification('Category added', 'success');
                          } else {
                            showNotification(result.error || 'Failed to add category', 'error');
                          }
                        }}
                        disabled={isAddingCategory}
                        className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                      >
                        {isAddingCategory ? 'Adding...' : 'Add'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddCategoryInput(false);
                          setNewCategoryLabel('');
                        }}
                        className="px-2 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Risk Level *
                  </label>
                  <select
                    required
                    value={formData.riskLevel}
                    onChange={(e) => setFormData({ ...formData, riskLevel: e.target.value as 'High' | 'Medium' | 'Low' })}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                  </select>
                </div>
              </div>
            )}

            {/* Owner Role Display - Simplified for checklist */}
            {isFromChecklist && prepopulatedOwnerRole ? (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Owner Role</label>
                <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                  <span className="text-sm font-medium text-blue-900">
                    {prepopulatedOwnerRole}
                  </span>
                </div>
              </div>
            ) : (
              /* Owner Role Selection (multi-select, same fields as Android) */
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Owner Roles (Optional)
                </label>
                <div className="border border-gray-300 rounded-md max-h-40 overflow-y-auto">
                  <div className="p-2 space-y-1">
                    {availableOwnerRoles.map(role => {
                      const isSelected = formData.ownerRoles.includes(role);
                      return (
                        <label
                          key={role}
                          className="flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                              setFormData(prev => ({
                                ...prev,
                                ownerRoles: isSelected
                                  ? prev.ownerRoles.filter(r => r !== role)
                                  : [...prev.ownerRoles, role],
                              }));
                            }}
                            className="rounded border-gray-300"
                          />
                          <span className="text-sm">{role}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
                {formData.ownerRoles.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.ownerRoles.length} role{formData.ownerRoles.length !== 1 ? 's' : ''} selected
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Or assign to specific users below
                </p>
              </div>
            )}

            {/* Assign to Users - Hide for checklist */}
            {!isFromChecklist && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Assign to Users (Optional)
                </label>
              <div className="relative mb-2">
                <Search className="absolute left-2 top-2.5 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  placeholder="Search users by name or email..."
                  className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-300 rounded-md"
                />
              </div>
              <div className="border border-gray-300 rounded-md max-h-48 overflow-y-auto">
                {filteredUsers.length === 0 ? (
                  <div className="p-3 text-sm text-gray-500 text-center">
                    No users found
                  </div>
                ) : (
                  <div className="space-y-1 p-2">
                    {filteredUsers.map(user => {
                      const isSelected = formData.assignedUserIds.includes(user.id);
                      return (
                        <label
                          key={user.id}
                          className={`
                            flex items-center space-x-2 p-2 rounded cursor-pointer hover:bg-gray-50
                            ${isSelected ? 'bg-primary-50 border border-primary-300' : 'border border-transparent'}
                          `}
                        >
                          <div className="flex items-center justify-center w-5 h-5">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleUser(user.id)}
                              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                            />
                          </div>
                          <div className="flex-1 min-w-0 flex items-center space-x-2">
                            {user.picture && (
                              <img 
                                src={user.picture} 
                                alt={user.name}
                                className="w-6 h-6 rounded-full flex-shrink-0"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2">
                                <span className={`text-sm font-medium ${isSelected ? 'text-primary-900' : 'text-gray-900'}`}>
                                  {user.name || 'No name'}
                                </span>
                                {isSelected && (
                                  <Check className="w-4 h-4 text-primary-600 flex-shrink-0" />
                                )}
                              </div>
                              <div className="text-xs text-gray-500 truncate">
                                {user.email}
                              </div>
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              {formData.assignedUserIds.length > 0 && (
                <div className="mt-2 text-xs text-gray-600">
                  {formData.assignedUserIds.length} user{formData.assignedUserIds.length !== 1 ? 's' : ''} selected
                </div>
              )}
              </div>
            )}

            {/* Evidence Types */}
            <div>
              <div className="flex items-start justify-between gap-3">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Evidence Types
                </label>
              </div>
              <div className="space-y-1.5 mb-1.5">
                <div className="flex gap-1.5 items-center">
                  <select
                    value={evidenceType.type}
                    onChange={(e) => setEvidenceType({ ...evidenceType, type: e.target.value })}
                    className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  >
                    <option value="">Type...</option>
                    {EVIDENCE_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={evidenceType.label}
                    onChange={(e) => setEvidenceType({ ...evidenceType, label: e.target.value })}
                    placeholder="Label"
                    className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  />
                  <label className="flex items-center whitespace-nowrap text-xs text-gray-700">
                    <input
                      type="checkbox"
                      checked={evidenceType.required}
                      onChange={(e) => setEvidenceType({ ...evidenceType, required: e.target.checked })}
                      className="mr-1"
                    />
                    Req.
                  </label>
                  <button
                    type="button"
                    onClick={addEvidenceType}
                    className="btn-primary px-2 py-1.5 text-sm"
                    disabled={!evidenceType.type || !evidenceType.label}
                  >
                    Add
                  </button>
                </div>
                {formData.evidenceTypes.length > 0 && (
                  <div className="space-y-1">
                    {formData.evidenceTypes.map((evidence, index) => (
                      <div key={index} className="bg-gray-100 px-2 py-1 rounded text-xs flex items-center justify-between">
                        <span>
                          <strong>{evidence.label}</strong> ({evidence.type})
                          {evidence.required && <span className="text-red-600 ml-1">*</span>}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeEvidenceType(index)}
                          className="text-gray-600 hover:text-gray-800 ml-2"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Pass/Fail Criteria */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Pass/Fail Criteria
              </label>
              <textarea
                value={formData.passFailCriteria}
                onChange={(e) => setFormData({ ...formData, passFailCriteria: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                rows={3}
                placeholder="Describe the criteria for passing or failing this task..."
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Description / Notes
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                rows={3}
                placeholder="Additional notes or description..."
              />
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                {getButtonText()}
              </button>
            </div>
          </form>
        </div>
      </div>
      
      {/* Notes Step - Show when there are reference images */}
      {currentStep === 'notes' && hasReferenceImages && (
        <>
          {/* Backdrop */}
          <div 
            className={`fixed inset-0 bg-black bg-opacity-50 ${backdropZIndex} transition-opacity`}
            onClick={handleBack}
          />
          
          {/* Notes Panel */}
          <div className={`fixed inset-y-0 right-0 w-[600px] bg-white shadow-2xl border-l border-gray-200 ${panelZIndex} flex flex-col`}>
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
              <h2 className="text-lg font-semibold text-gray-800">Add Notes for Reference Images</h2>
              <button
                onClick={handleBack}
                className="text-gray-500 hover:text-gray-700 p-1"
                title="Back"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Notes Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                Add optional notes for each reference image to provide context or instructions.
              </p>
              
              {/* Prepopulated Images */}
              {prepopulatedImageUrls.map((url, index) => {
                const note = referenceImageNotes.get(url) || '';
                return (
                  <div key={`prepop-${index}`} className="border border-gray-300 rounded-md p-3 space-y-2">
                    <div className="flex items-start gap-3">
                      <img
                        src={url}
                        alt={`Reference image ${index + 1}`}
                        className="w-32 h-32 object-cover rounded border border-gray-200 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium text-gray-700">Image {index + 1}</span>
                          <span className="text-xs text-gray-500">(From message)</span>
                        </div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Notes (optional)
                        </label>
                        <textarea
                          value={note}
                          onChange={(e) => {
                            setReferenceImageNotes(prev => {
                              const updated = new Map(prev);
                              if (e.target.value.trim()) {
                                updated.set(url, e.target.value);
                              } else {
                                updated.delete(url);
                              }
                              return updated;
                            });
                          }}
                          placeholder="Add notes about this image..."
                          rows={3}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {/* Uploaded Images */}
              {referenceImages.map((file, index) => {
                const preview = imagePreviews[index];
                const note = referenceImageNotes.get(file.name) || '';
                return (
                  <div key={`upload-${index}`} className="border border-gray-300 rounded-md p-3 space-y-2">
                    <div className="flex items-start gap-3">
                      {preview && (
                        <img
                          src={preview}
                          alt={file.name}
                          className="w-32 h-32 object-cover rounded border border-gray-200 flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium text-gray-700 truncate">{file.name}</span>
                          <span className="text-xs text-gray-500 flex-shrink-0">
                            ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                        </div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Notes (optional)
                        </label>
                        <textarea
                          value={note}
                          onChange={(e) => {
                            setReferenceImageNotes(prev => {
                              const updated = new Map(prev);
                              if (e.target.value.trim()) {
                                updated.set(file.name, e.target.value);
                              } else {
                                updated.delete(file.name);
                              }
                              return updated;
                            });
                          }}
                          placeholder="Add notes about this image..."
                          rows={3}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Notes Actions */}
            <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleBack}
                className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={isSubmitting}
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating...' : 'Create Task' + (formData.locationIds.length > 1 ? `s (${formData.locationIds.length})` : '')}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default AddTaskPanel;
